import type { StyleSpecification } from "maplibre-gl";
import Color from "color";
import { classifyLayer } from "./classify";
import { findColors, normalizeColor, parseColor, solidColor } from "./colors";
import { layerById, readPath, rewrite, roleOf, ZoomSide, zoomSideOf } from "./paths";
import type { ColorPath, ComponentId, Role } from "./types";

// A main colour is one named swatch that drives a whole family of shades,
// for example "Water" for the sea, lakes and rivers. Changing it moves every
// shade in the family by the same amount, so their differences are kept.
export type MainColor = {
  id: string;
  title: string;
  component: ComponentId;
  paths: ColorPath[];
  // The place whose colour the swatch shows: the family's most used shade
  // when the style was read (filled areas first). Fixed afterwards, so the
  // swatch never jumps to another shade while the user edits.
  anchor: ColorPath;
  // The places of `paths` and `anchor` regardless of zoom splits, so a
  // rebuild after a split can keep the family and its swatch as they were.
  memberKeys: string[];
  anchorKey: string;
};

type Occurrence = {
  component: ComponentId;
  section: string;
  role: Role;
  layerId: string;
  context?: string;
  color: string;
  path: ColorPath;
  // The end of a two-stop zoom blend the colour sits in, if any.
  zoomSide: ZoomSide | null;
};

type Definition = {
  id: string;
  title: string;
  component: ComponentId;
  match(occurrence: Occurrence): boolean;
};

const GREEN =
  /park|wood|forest|scrub|grass|farmland|green|zoo|golf|cemetery|nature|protected|military/;

const isArea = (o: Occurrence) =>
  o.section === "landuse" || o.section === "landcover";

// Order matters: an occurrence belongs to the first definition it matches.
const DEFINITIONS: Definition[] = [
  {
    id: "land",
    title: "Land",
    component: "land",
    match: (o) => o.section === "background" || o.section === "land",
  },
  {
    id: "water",
    title: "Water",
    component: "water",
    match: (o) => o.component === "water",
  },
  {
    id: "green",
    title: "Green areas",
    component: "landuse",
    match: (o) => isArea(o) && GREEN.test(o.context || o.layerId),
  },
  {
    id: "areas",
    title: "Other areas",
    component: "landuse",
    match: isArea,
  },
  {
    id: "motorways",
    title: "Motorways",
    component: "roads",
    match: (o) => o.section === "surface" && /highway|motorway/.test(o.layerId),
  },
  {
    id: "main_roads",
    title: "Main roads",
    component: "roads",
    match: (o) =>
      o.section === "surface" && /major|link|trunk|primary/.test(o.layerId),
  },
  {
    id: "small_roads",
    title: "Small roads",
    component: "roads",
    match: (o) => o.section === "surface",
  },
  {
    id: "road_outlines",
    title: "Road outlines",
    component: "roads",
    match: (o) => o.section === "outlines",
  },
  {
    id: "tunnels",
    title: "Tunnels",
    component: "roads",
    match: (o) => o.section === "tunnels",
  },
  {
    id: "rail_airport",
    title: "Rail, airport & piers",
    component: "roads",
    match: (o) => o.section === "rail_airport",
  },
  {
    id: "buildings",
    title: "Buildings",
    component: "buildings",
    match: (o) => o.component === "buildings",
  },
  {
    id: "borders",
    title: "Borders",
    component: "borders",
    match: (o) => o.component === "borders",
  },
  {
    id: "place_names",
    title: "Place names",
    component: "labels",
    match: (o) => o.section === "places" && o.role === "text",
  },
  {
    id: "road_names",
    title: "Road names",
    component: "labels",
    match: (o) => o.section === "road_names" && o.role === "text",
  },
  {
    id: "water_names",
    title: "Water names",
    component: "labels",
    match: (o) => o.section === "water_names" && o.role === "text",
  },
  {
    id: "label_outlines",
    title: "Label outlines",
    component: "labels",
    match: (o) =>
      (o.component === "labels" || o.component === "pois") && o.role === "halo",
  },
  {
    id: "poi_labels",
    title: "Points of interest",
    component: "pois",
    match: (o) => o.component === "pois" && o.role === "text",
  },
];

function listOccurrences(style: StyleSpecification): Occurrence[] {
  const out: Occurrence[] = [];
  (style.layers || []).forEach((layer) => {
    const { component, section } = classifyLayer(layer);
    const paint = (layer as { paint?: { [prop: string]: unknown } }).paint || {};
    Object.keys(paint)
      .filter((prop) => prop.endsWith("-color"))
      .forEach((prop) => {
        findColors(paint[prop]).forEach((found) => {
          out.push({
            component,
            section,
            role: roleOf(prop),
            layerId: layer.id,
            context: found.context,
            color: normalizeColor(found.color),
            path: { layerId: layer.id, prop, path: found.path },
            zoomSide: null,
          });
        });
      });
  });
  out.forEach((occurrence) => {
    occurrence.zoomSide = zoomSideOf(style, occurrence.path);
  });
  return out;
}

// Where a colour sits, ignoring whether its value was split by zoom: both
// ends of a split share the key of the colour they came from.
function memberKey(o: Occurrence): string {
  const path = o.zoomSide ? o.path.path.slice(1) : o.path.path;
  return `${o.layerId}|${o.path.prop}|${JSON.stringify(path)}`;
}

// Sorts every colour in the style into at most one main colour. Colours in
// components Simple mode does not recognise ("other") belong to none.
//
// `previous` is the result of an earlier call for the same style, before a
// change of its structure (a zoom split). Colours keep the family they had
// and each family keeps the shade its swatch showed; only new places are
// sorted by the rules. Without it, families follow from the colours alone,
// which can differ once the user has recoloured things.
export function buildMainColors(
  style: StyleSpecification,
  previous?: MainColor[]
): MainColor[] {
  const occurrences = listOccurrences(style);
  const knownFamily = new Map<string, string>();
  previous?.forEach((main) => {
    main.memberKeys.forEach((key) => knownFamily.set(key, main.id));
  });

  // Area fills painted exactly like the land are "no special colour" and
  // must keep following the land. When the land has a far and a close
  // colour, an area is compared with the land at the same end.
  const landColorAt = (side: ZoomSide | null) =>
    occurrences.find((o) => o.section === "land" && o.zoomSide === side)?.color;
  const landColor = landColorAt(null);
  const groups = new Map<string, Occurrence[]>();

  occurrences.forEach((occurrence) => {
    const land =
      (occurrence.zoomSide && landColorAt(occurrence.zoomSide)) || landColor;
    const followsLand =
      land !== undefined && isArea(occurrence) && occurrence.color === land;
    const known = knownFamily.get(memberKey(occurrence));
    const definition =
      (known && DEFINITIONS.find((candidate) => candidate.id === known)) ||
      (followsLand
        ? DEFINITIONS[0]
        : DEFINITIONS.find((candidate) => candidate.match(occurrence)));
    if (!definition) return;
    groups.set(definition.id, [...(groups.get(definition.id) || []), occurrence]);
  });

  return DEFINITIONS.filter((definition) => groups.has(definition.id)).map(
    ({ id, title, component }) => {
      const members = groups.get(id)!;
      const before = previous?.find((main) => main.id === id)?.anchorKey;
      const anchor =
        (before && members.find((member) => memberKey(member) === before)) ||
        mostUsed(members);
      return {
        id,
        title,
        component,
        paths: members.map((member) => member.path),
        anchor: anchor.path,
        memberKeys: members.map(memberKey),
        anchorKey: memberKey(anchor),
      };
    }
  );
}

const AREA_PROPS = ["fill-color", "background-color"];

// The first occurrence of the most frequent colour. Filled areas are what
// the eye reads as "the colour of the water", so they are preferred over
// lines when a family has both. Ties go to the colour that comes first.
function mostUsed(members: Occurrence[]): Occurrence {
  const areas = members.filter((member) => AREA_PROPS.includes(member.path.prop));
  const candidates = areas.length > 0 ? areas : members;

  const counts = new Map<string, number>();
  candidates.forEach((member) => {
    counts.set(member.color, (counts.get(member.color) || 0) + 1);
  });
  let best = candidates[0];
  candidates.forEach((member) => {
    if (counts.get(member.color)! > counts.get(best.color)!) best = member;
  });
  return best;
}

// The colour shown on the swatch.
export function getMainColor(
  style: StyleSpecification,
  main: MainColor
): string | undefined {
  const value = readPath(layerById(style, main.anchor.layerId), main.anchor);
  return typeof value === "string" ? normalizeColor(value) : undefined;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

// Below this saturation a colour is grey and its hue carries no meaning.
const GREY_SATURATION = 3;

type Hsl = { h: number; s: number; l: number; alpha: number };

function toHsl(value: string): Hsl | undefined {
  const color = parseColor(value);
  if (!color) return undefined;
  const hsl = color.hsl();
  return {
    h: hsl.hue(),
    s: hsl.saturationl(),
    l: hsl.lightness(),
    alpha: color.alpha(),
  };
}

function fromHsl({ h, s, l, alpha }: Hsl): string {
  return normalizeColor(
    Color.hsl(((h % 360) + 360) % 360, clamp(s, 0, 100), clamp(l, 0, 100))
      .alpha(alpha)
      .rgb()
      .string()
  );
}

// Sets a main colour. The swatch's own shade becomes exactly `color`; every
// other shade in the family moves by the same hue, saturation and lightness
// difference. Always call this on the style from BEFORE a drag started, not
// on the previous preview, or rounding and clamping would add up.
//
// A main colour is a solid colour: only the hue, saturation and lightness of
// `color` are used. Every place in the family keeps the transparency it has.
export function setMainColor<S extends StyleSpecification>(
  style: S,
  main: MainColor,
  color: string
): S {
  const fromColor = getMainColor(style, main);
  const from = fromColor && toHsl(fromColor);
  const to = toHsl(color);
  if (!fromColor || !from || !to) return style;
  const target = solidColor(color);
  const fromSolid = solidColor(fromColor);
  if (target === fromSolid) return style;

  return rewrite(style, main.paths, (current) => {
    const shade = toHsl(current);
    if (!shade) return current;
    if (solidColor(current) === fromSolid) {
      // The swatch's shade lands exactly, without a detour through HSL.
      return shade.alpha === 1
        ? target
        : normalizeColor(Color(target).alpha(shade.alpha).rgb().string());
    }
    return fromHsl({
      h: shade.s < GREY_SATURATION ? to.h : shade.h + (to.h - from.h),
      s: shade.s + (to.s - from.s),
      l: shade.l + (to.l - from.l),
      alpha: shade.alpha,
    });
  });
}

export type Adjustment = {
  // Degrees to rotate every hue, -180 to 180.
  hue: number;
  // -100 removes all colour, 0 leaves it, 100 doubles saturation.
  saturation: number;
  // Brightness, -50 to 50: above zero every colour is mixed toward white,
  // below zero toward black, by twice this many percent (50 is all white,
  // -50 all black). Unlike adding HSL lightness, it never makes a colour
  // more vivid.
  lightness: number;
};

export const NO_ADJUSTMENT: Adjustment = { hue: 0, saturation: 0, lightness: 0 };

// Shifts every colour in the style, recognised or not. As with setMainColor,
// apply it to the style from before the slider moved.
export function adjustAllColors<S extends StyleSpecification>(
  style: S,
  adjustment: Adjustment
): S {
  const paths = listOccurrences(style).map((occurrence) => occurrence.path);
  return rewrite(style, paths, (current) => {
    const shade = toHsl(current);
    if (!shade) return current;
    const shifted = {
      ...shade,
      h: shade.h + adjustment.hue,
      s: shade.s * (1 + adjustment.saturation / 100),
    };
    if (adjustment.lightness === 0) return fromHsl(shifted);

    // Brightness is a mix toward white or black in RGB, after the hue and
    // saturation changes.
    const amount = Math.min(1, (Math.abs(adjustment.lightness) / 100) * 2);
    const target = adjustment.lightness > 0 ? 255 : 0;
    const [r, g, b] = Color.hsl(
      ((shifted.h % 360) + 360) % 360,
      clamp(shifted.s, 0, 100),
      shifted.l
    )
      .rgb()
      .array()
      .map((channel) => channel + (target - channel) * amount);
    return normalizeColor(Color.rgb(r, g, b).alpha(shade.alpha).rgb().string());
  });
}
